import React from "react";
import {
  FaBan,
  FaCheck,
  FaCheckDouble,
  FaCircle,
  FaExclamation,
  FaExternalLinkAlt,
  FaMinus,
  FaPaperPlane,
  FaPlay,
} from "react-icons/fa";
import { FiArrowUpRight } from "react-icons/fi";

export const SERVICE_LABELS = { CNT: "Content", SEO: "SEO", SMO: "SMO", SEM: "SEM", SMM: "SMM" };

/** Owner values are stored as Agency / Client; the agency side is shown as Cloud Office. */
export const ownerLabel = (owner) => (owner === "Agency" ? "Cloud Office" : owner);

/** Organic services (SEO, SMO, Content); the paid ones (SEM, SMM) are Campaigns. */
const ORGANIC_SERVICES = ["SEO", "SMO", "CNT"];

export function serviceFamily(services) {
  if (!services?.length) return null;
  return services.some((s) => ORGANIC_SERVICES.includes(s)) ? "Organics" : "Campaigns";
}

/** Group name prefixed with its own service when the name doesn't already say it (e.g. "SMO / Social Accounts"). */
export function groupLabel(category) {
  const name = category?.Name || "";
  const own = SERVICE_LABELS[category?.Code];
  return own && !name.includes(own) ? `${own} / ${name}` : name;
}

/** Which DM services a group belongs to: one Organics / Campaigns oval ("All clients" when shared). */
export function GroupServices({ services }) {
  if (!services?.length) return <span className="dms-svc dms-svc--all">All clients</span>;
  const family = serviceFamily(services);
  return (
    <span
      className={`dms-svc dms-svc--${family.toLowerCase()}`}
      title={`For clients with ${services.map((s) => SERVICE_LABELS[s] || s).join(" or ")}`}
    >
      {family}
    </span>
  );
}

/** Opens an account / profile in a new tab without giving it access to this window. */
/** ``chip`` draws the label with an up-right arrow inside one small outlined button. */
export function OpenLink({ href, label = "Open", className = "", title, chip = false }) {
  if (!href) return null;
  return (
    <a
      className={`dms-open${chip ? " dms-open--chip" : ""} ${className}`.trim()}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={title || `${label} (opens in a new window)`}
      onClick={(e) => e.stopPropagation()}
    >
      {label} {chip ? <FiArrowUpRight aria-hidden="true" /> : <FaExternalLinkAlt aria-hidden="true" />}
    </a>
  );
}

export const DONE_STATUSES = ["Done", "Verified"];
export const NOT_APPLICABLE = "Not Applicable";

const STATUS_STYLE = {
  Verified: { tone: "verified", Icon: FaCheckDouble },
  Done: { tone: "done", Icon: FaCheck },
  Submitted: { tone: "submitted", Icon: FaPaperPlane },
  "In Progress": { tone: "progress", Icon: FaPlay },
  "Waiting on Client": { tone: "waiting", Icon: FaExclamation },
  Blocked: { tone: "blocked", Icon: FaBan },
  "Not Started": { tone: "todo", Icon: FaCircle },
  "Not Applicable": { tone: "na", Icon: FaMinus },
};

export const statusStyle = (status) => STATUS_STYLE[status] || STATUS_STYLE["Not Started"];

export const isDone = (status) => DONE_STATUSES.includes(status);

export const isOpen = (step) => !isDone(step.Status) && step.Status !== NOT_APPLICABLE;

/** Status with colour, icon and words (never colour alone). */
export function SetupStatus({ status, small = false }) {
  const { tone, Icon } = statusStyle(status);
  return (
    <span className={`dms-status dms-status--${tone}${small ? " dms-status--sm" : ""}`}>
      <Icon aria-hidden="true" />
      {status}
    </span>
  );
}

export function StatusDot({ status }) {
  const { tone, Icon } = statusStyle(status);
  return (
    <span className={`dms-dot dms-dot--${tone}`} aria-hidden="true">
      <Icon />
    </span>
  );
}

export function ProgressBar({ pct, label }) {
  const value = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <span
      className={`dms-bar${value === 100 ? " is-complete" : ""}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      aria-label={label || `${value}% complete`}
    >
      <span style={{ width: `${value}%` }} />
    </span>
  );
}

/** Compact "62%" with a mini bar, for client lists. */
export function SetupPctPill({ pct }) {
  if (pct == null) return <span className="dmp-muted">—</span>;
  return (
    <span className={`dms-pct${pct === 100 ? " is-complete" : ""}`} title={`Setup ${pct}% complete`}>
      <ProgressBar pct={pct} label={`Setup ${pct}% complete`} />
      <span>{pct}%</span>
    </span>
  );
}

export function ProgressRing({ pct, size = 128 }) {
  const value = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div
      className="dms-ring"
      style={{ width: size, height: size, "--dms-ring": `${value * 3.6}deg` }}
      role="img"
      aria-label={`${value}% of setup complete`}
    >
      <div className="dms-ring__inner">
        <strong>{value}%</strong>
        <span>complete</span>
      </div>
    </div>
  );
}

/** First captured value of a step (shown under its title in the list). */
export function stepSummary(step) {
  const data = step.Data || {};
  for (const field of step.Fields || []) {
    if (["secret", "file", "checkbox"].includes(field.type)) continue;
    const value = data[field.key];
    if (value != null && String(value).trim() !== "") return String(value);
  }
  return null;
}

export const fmtShort = (iso) => {
  if (!iso) return "";
  const d = new Date(String(iso).length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

export const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
};

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  }
}
